// SCR-STAFF-875 (AP-INV-02؛ US-INV-043): انبارگردانی — برنامه انبار یا دسته با امکان شمارش کور، شمارش روی تبلت با اسکن بارکد/GS1،
// بازبینی مغایرت با لحاظ گردش‌های حین شمارش (AC-INV-043.1) و تأیید با سند اصلاح (AC-INV-043.2؛ BR-INV-041).
import AddIcon from '@mui/icons-material/Add';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { unwrap, type InvStocktakeLineView, type InvStocktakeView } from '@phoenix/api-client';
import { useAccess, useIdempotencyKey, useTenantSession, useWriteAccess } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { DataTable, FormDialog, ListPage, Money, StatusChip, faDigits, type DataTableColumn, type StatusTone } from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { WriteNotice } from '../settings/common';
import { fromMilli, STOCKTAKES_PATH, stockKeys, toMilli, useQuantityText, useWarehouses, WarehouseSelect, warehouseName } from './stockCommon';

export const SCR_STAFF_875 = 'SCR-STAFF-875';

export function stocktakeTone(status: string): StatusTone {
  switch (status) {
    case 'Planned':
      return 'booked';
    case 'Counting':
      return 'in_service';
    case 'Review':
      return 'pending';
    default:
      return 'done';
  }
}

export function StocktakesPage() {
  const { t } = useTranslation('inv');
  const { tenantApi, session } = useTenantSession();
  const navigate = useNavigate();
  const warehouses = useWarehouses();
  const write = useWriteAccess('inv.stocktake.manage');
  const [creating, setCreating] = useState(false);
  const stocktakes = useQuery({
    queryKey: stockKeys.stocktakes(session?.tenantId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/inventory/stocktakes')),
  });

  const columns: DataTableColumn<InvStocktakeView>[] = [
    {
      id: 'number',
      headerKey: 'inv:stocktakes.columns.number',
      cell: (row) => (
        <Button size="small" onClick={() => navigate(`${STOCKTAKES_PATH}/${row.id}`)} data-testid={`stocktake-${row.number}`}>
          <bdi dir="ltr">{row.number}</bdi>
        </Button>
      ),
    },
    { id: 'warehouse', headerKey: 'inv:stocktakes.columns.warehouse', cell: (row) => warehouseName(warehouses.data?.items, row.warehouseId) },
    { id: 'blind', headerKey: 'inv:stocktakes.columns.blind', cell: (row) => (row.isBlind ? t('inv:stocktakes.yes') : t('inv:stocktakes.no')) },
    { id: 'status', headerKey: 'inv:stocktakes.columns.status', cell: (row) => <StatusChip tone={stocktakeTone(row.status)} labelKey={`inv:stocktake_status.${row.status}`} /> },
    { id: 'shortage', headerKey: 'inv:stocktakes.columns.shortage', cell: (row) => <Money amountMinor={row.shortageValue.amount} currency={row.shortageValue.currency} /> },
    { id: 'surplus', headerKey: 'inv:stocktakes.columns.surplus', cell: (row) => <Money amountMinor={row.surplusValue.amount} currency={row.surplusValue.currency} /> },
  ];

  return (
    <ListPage
      screenId={SCR_STAFF_875}
      titleKey="inv:stocktakes.title"
      subtitleKey="inv:stocktakes.subtitle"
      actions={
        <Button variant="contained" startIcon={<AddIcon />} disabled={!write.canWrite} onClick={() => setCreating(true)} data-testid="stocktake-create">
          {t('inv:stocktakes.add')}
        </Button>
      }
    >
      <WriteNotice write={write} />
      <DataTable
        captionKey="inv:stocktakes.caption"
        columns={columns}
        rows={stocktakes.data}
        getRowId={(row) => row.id}
        isLoading={stocktakes.isPending}
        error={stocktakes.error}
        onRetry={() => void stocktakes.refetch()}
        emptyTitleKey="inv:stocktakes.empty"
      />
      {creating && <PlanDialog onClose={() => setCreating(false)} onCreated={(id) => navigate(`${STOCKTAKES_PATH}/${id}`)} />}
    </ListPage>
  );
}

function PlanDialog({ onClose, onCreated }: { onClose: () => void; onCreated: (id: string) => void }) {
  const { t } = useTranslation('inv');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const idempotency = useIdempotencyKey();
  const [warehouseId, setWarehouseId] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [isBlind, setIsBlind] = useState(true);
  const categories = useQuery({
    queryKey: ['inventory', 'item-categories', session?.tenantId],
    queryFn: () => unwrap(tenantApi.GET('/api/v1/inventory/item-categories')),
  });
  const save = useMutation({
    mutationFn: () =>
      unwrap(
        tenantApi.POST('/api/v1/inventory/stocktakes', {
          body: { warehouseId, categoryId: categoryId || null, isBlind },
          headers: { 'Idempotency-Key': idempotency.current() },
        }),
      ),
    onSettled: (_data, error) => idempotency.settle(error ?? undefined),
    onSuccess: (created) => {
      void queryClient.invalidateQueries({ queryKey: stockKeys.stocktakes(session?.tenantId) });
      onCreated(created.id);
    },
  });
  return (
    <FormDialog open screenId="SCR-STAFF-875-PLAN" titleKey="inv:stocktakes.plan_title" onSubmit={() => save.mutate()} onClose={onClose} isDirty={Boolean(warehouseId)} isSubmitting={save.isPending} submitError={save.error}>
      <WarehouseSelect value={warehouseId} onChange={setWarehouseId} required testId="stocktake-warehouse" />
      <TextField select label={t('inv:stocktakes.category')} value={categoryId} onChange={(event) => setCategoryId(event.target.value)} slotProps={{ htmlInput: { 'data-testid': 'stocktake-category' } }}>
        <MenuItem value="">{t('inv:items.filters.all')}</MenuItem>
        {(categories.data?.items ?? []).map((category) => (
          <MenuItem key={category.id} value={category.id}>
            {category.name}
          </MenuItem>
        ))}
      </TextField>
      <FormControlLabel control={<Switch checked={isBlind} onChange={(event) => setIsBlind(event.target.checked)} data-testid="stocktake-blind" />} label={t('inv:stocktakes.blind_label')} />
    </FormDialog>
  );
}

/** برگه شمارش (SCR-STAFF-875، مسیر /inventory/stocktakes/:id). */
export function StocktakePage() {
  const { id = '' } = useParams();
  const { t } = useTranslation('inv');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const access = useAccess();
  const warehouses = useWarehouses();
  const quantity = useQuantityText();
  const write = useWriteAccess('inv.stocktake.manage');
  const idempotency = useIdempotencyKey();
  const countIdempotency = useIdempotencyKey();
  const [scan, setScan] = useState('');
  const [scanQuantity, setScanQuantity] = useState('1');
  const [counts, setCounts] = useState<Record<string, string>>({});
  const key = [...stockKeys.stocktakes(session?.tenantId), id] as const;
  const stocktake = useQuery({
    queryKey: key,
    queryFn: () => unwrap(tenantApi.GET('/api/v1/inventory/stocktakes/{id}', { params: { path: { id } } })),
    enabled: Boolean(id),
  });
  const onSaved = (view: InvStocktakeView) => {
    queryClient.setQueryData(key, view);
    void queryClient.invalidateQueries({ queryKey: stockKeys.stocktakes(session?.tenantId), exact: true });
  };
  const options = { params: { path: { id } } };
  const action = useMutation({
    mutationFn: (verb: 'start' | 'submit' | 'recount' | 'approve') => {
      const headers = { 'Idempotency-Key': idempotency.current() };
      switch (verb) {
        case 'start':
          return unwrap(tenantApi.POST('/api/v1/inventory/stocktakes/{id}:start', { ...options, headers }));
        case 'submit':
          return unwrap(tenantApi.POST('/api/v1/inventory/stocktakes/{id}:submit', { ...options, headers }));
        case 'recount':
          return unwrap(tenantApi.POST('/api/v1/inventory/stocktakes/{id}:recount', { ...options, headers }));
        default:
          return unwrap(tenantApi.POST('/api/v1/inventory/stocktakes/{id}:approve', { ...options, headers }));
      }
    },
    onSettled: (_data, error) => idempotency.settle(error ?? undefined),
    onSuccess: onSaved,
  });
  const count = useMutation({
    mutationFn: (body: { itemId: string | null; batchId: string | null; scan: string | null; countedMilli: number }) =>
      unwrap(tenantApi.POST('/api/v1/inventory/stocktakes/{id}:count', { params: { path: { id } }, body: { ...body, unit: null }, headers: { 'Idempotency-Key': countIdempotency.current() } })),
    onSettled: (_data, error) => countIdempotency.settle(error ?? undefined),
    onSuccess: (view) => {
      onSaved(view);
      setScan('');
    },
  });

  const view = stocktake.data;
  const counting = view?.status === 'Counting';
  const reviewed = view && ['Review', 'Approved', 'Posted'].includes(view.status);
  const lineKey = (line: InvStocktakeLineView) => `${line.itemId}-${line.batchId ?? ''}`;
  // شناسه آزمون یکتا به ازای کالا و بچ (یک کالا با چند بچ چند ردیف دارد).
  const testKey = (line: InvStocktakeLineView) => (line.batchNumber ? `${line.itemId}-${line.batchNumber}` : line.itemId);

  const columns: DataTableColumn<InvStocktakeLineView>[] = [
    { id: 'item', headerKey: 'inv:stocktakes.lines.item', cell: (row) => row.itemName },
    { id: 'batch', headerKey: 'inv:stocktakes.lines.batch', cell: (row) => <bdi dir="ltr">{row.batchNumber ?? '—'}</bdi> },
    { id: 'system', headerKey: 'inv:stocktakes.lines.system', cell: (row) => (row.systemMilli === null ? <span data-testid="stocktake-system-hidden">{t('inv:stocktakes.hidden')}</span> : quantity(row.systemMilli, row.baseUnit)) },
    {
      id: 'counted',
      headerKey: 'inv:stocktakes.lines.counted',
      cell: (row) =>
        counting && write.canWrite ? (
          <Stack direction="row" spacing={1} alignItems="center">
            <TextField
              size="small"
              value={counts[lineKey(row)] ?? (row.countedMilli === null ? '' : String(fromMilli(row.countedMilli)))}
              onChange={(event) => setCounts((current) => ({ ...current, [lineKey(row)]: event.target.value }))}
              sx={{ width: 110 }}
              slotProps={{ htmlInput: { inputMode: 'decimal', dir: 'ltr', 'aria-label': t('inv:stocktakes.lines.counted'), 'data-testid': `stocktake-count-${testKey(row)}` } }}
            />
            <Button
              size="small"
              disabled={count.isPending || toMilli(counts[lineKey(row)] ?? '') === null}
              onClick={() => count.mutate({ itemId: row.itemId, batchId: row.batchId, scan: null, countedMilli: toMilli(counts[lineKey(row)] ?? '') ?? 0 })}
              data-testid={`stocktake-count-save-${testKey(row)}`}
            >
              {t('inv:stocktakes.save_count')}
            </Button>
          </Stack>
        ) : row.countedMilli === null ? (
          '—'
        ) : (
          quantity(row.countedMilli, row.baseUnit)
        ),
    },
    { id: 'moved', headerKey: 'inv:stocktakes.lines.moved', cell: (row) => quantity(row.movedMilli, row.baseUnit) },
    {
      id: 'variance',
      headerKey: 'inv:stocktakes.lines.variance',
      cell: (row) => (reviewed ? <span data-testid={`stocktake-variance-${testKey(row)}`}>{quantity(row.varianceMilli, row.baseUnit)}</span> : '—'),
    },
    { id: 'value', headerKey: 'inv:stocktakes.lines.value', cell: (row) => (reviewed ? <Money amountMinor={row.varianceValue.amount} currency={row.varianceValue.currency} /> : '—') },
  ];

  return (
    <ListPage
      screenId={SCR_STAFF_875}
      titleKey="inv:stocktakes.sheet_title"
      titleValues={{ number: view?.number ?? '' }}
      subtitleKey="inv:stocktakes.sheet_subtitle"
      badges={view && <StatusChip tone={stocktakeTone(view.status)} labelKey={`inv:stocktake_status.${view.status}`} />}
      actions={
        view && (
          <Stack direction="row" spacing={1}>
            {view.status === 'Planned' && (
              <Button variant="contained" disabled={!write.canWrite || action.isPending} onClick={() => action.mutate('start')} data-testid="stocktake-start">
                {t('inv:stocktakes.start')}
              </Button>
            )}
            {counting && (
              <Button variant="contained" disabled={!write.canWrite || action.isPending} onClick={() => action.mutate('submit')} data-testid="stocktake-submit">
                {t('inv:stocktakes.submit')}
              </Button>
            )}
            {view.status === 'Review' && (
              <>
                <Button disabled={!write.canWrite || action.isPending} onClick={() => action.mutate('recount')} data-testid="stocktake-recount">
                  {t('inv:stocktakes.recount')}
                </Button>
                <Button variant="contained" disabled={access.accessMode !== 'Full' || !access.can('inv.stocktake.approve') || action.isPending} onClick={() => action.mutate('approve')} data-testid="stocktake-approve">
                  {t('inv:stocktakes.approve')}
                </Button>
              </>
            )}
          </Stack>
        )
      }
      filters={
        view && (
          <Stack spacing={1}>
            <Typography variant="body2" color="text.secondary">
              {t('inv:stocktakes.columns.warehouse')}: {warehouseName(warehouses.data?.items, view.warehouseId)} · {view.isBlind ? t('inv:stocktakes.blind_label') : t('inv:stocktakes.open_count')}
            </Typography>
            {counting && write.canWrite && (
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                <TextField
                  size="small"
                  label={t('inv:scan.label')}
                  value={scan}
                  onChange={(event) => setScan(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' && scan.trim()) {
                      event.preventDefault();
                      count.mutate({ itemId: null, batchId: null, scan: scan.trim(), countedMilli: toMilli(scanQuantity) ?? 1000 });
                    }
                  }}
                  sx={{ minWidth: 320 }}
                  slotProps={{ htmlInput: { dir: 'ltr', 'data-testid': 'stocktake-scan' } }}
                />
                <TextField size="small" label={t('inv:stocktakes.scan_quantity')} value={scanQuantity} onChange={(event) => setScanQuantity(event.target.value)} sx={{ width: 140 }} slotProps={{ htmlInput: { inputMode: 'decimal', dir: 'ltr', 'data-testid': 'stocktake-scan-quantity' } }} />
              </Stack>
            )}
            {count.error && <Alert severity="error">{t('inv:stocktakes.count_failed')}</Alert>}
            {view.status === 'Posted' && (
              <Alert severity="success" data-testid="stocktake-posted">
                {t('inv:stocktakes.posted', { shortage: faDigits(String(view.shortageValue.amount)), surplus: faDigits(String(view.surplusValue.amount)) })}
              </Alert>
            )}
          </Stack>
        )
      }
    >
      <WriteNotice write={write} />
      {action.error && <Alert severity="error">{t('inv:stocktakes.action_failed')}</Alert>}
      <DataTable
        captionKey="inv:stocktakes.lines.caption"
        columns={columns}
        rows={view?.lines}
        getRowId={lineKey}
        isLoading={stocktake.isPending}
        error={stocktake.error}
        onRetry={() => void stocktake.refetch()}
        emptyTitleKey="inv:stocktakes.lines.empty"
      />
    </ListPage>
  );
}
